import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import ProductMediaViewer from './ProductMediaViewer';
import { createPaymentOrder, verifyPayment, simulateTestPayment, getPublicShopSettings } from '../utils/api';
import {
  submitShopInquiryFromCart,
  openShopInquiryWhatsAppFromCart,
} from '../utils/shopInquirySubmit';
import { useShopLanguage } from '../context/ShopLanguageContext';
import { cn } from '../lib/utils';
import { roundRupees } from '../utils/money';
import { readShopCart, writeShopCart, clearShopCart } from '../utils/shopCartStorage';
import { buildRazorpayCheckoutOptions } from '../utils/razorpayCheckout';
import { completePublicGalleryOrderAfterPayment } from '../utils/shopPaymentFlow';
import { downloadShopOrderReceipt } from '../utils/orderReceiptDownload';
import ShopToaster from './ShopToaster';
import ShopOrderFlowOverlay from './ShopOrderFlowOverlay';
import ShopWholesaleMinQtyCallout from './ShopWholesaleMinQtyCallout';
import { sectionVariants, usePrefersReducedMotion } from '../lib/motionShared';
import { formatModelCode } from '../utils/modelCategoryRanges';
import {
  getShopLineUnitPrice,
  getShopDisplayUnitPrice,
  calculateShopCartTotal,
  getShopMinimumAdvance,
} from '../utils/shopOrderType';
import { isShopItemOutOfStock } from '../utils/shopStockLabel';
import { useLockBodyScroll } from '../hooks/useLockBodyScroll';

const getPublicInventory = async () => {
  const response = await fetch('/api/inventory/public');
  if (!response.ok) throw new Error('Failed to fetch inventory');
  return response.json();
};

const createPublicOrder = async (orderData) => {
  const response = await fetch('/api/orders/public', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(orderData)
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to create order');
  }
  return response.json();
};

const getItemMediaSlides = (item) => {
  if (!item) return [{ src: '/logo.png' }];
  if (Array.isArray(item.photos) && item.photos.length > 0) {
    return item.photos.filter((photo) => typeof photo === 'string' && photo.trim()).map((photo) => ({ src: photo }));
  }
  if (typeof item.photo === 'string' && item.photo.trim()) {
    return [{ src: item.photo }];
  }
  return [{ src: '/logo.png' }];
};

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { locale, setLocale, t } = useShopLanguage();
  const itemName = (item) => {
    if (locale === 'mr' && item?.modelNameMr) return item.modelNameMr;
    if (locale === 'kn' && item?.modelNameKn) return item.modelNameKn;
    return item?.modelName;
  };
  const itemSize = (item) => item?.size ?? item?.category;
  const reduceMotion = usePrefersReducedMotion();
  const sectionMotion = useMemo(
    () =>
      reduceMotion
        ? { hidden: { opacity: 1, y: 0 }, visible: { opacity: 1, y: 0 } }
        : sectionVariants,
    [reduceMotion]
  );
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);
  const [inventory, setInventory] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [orderModalMode, setOrderModalMode] = useState('add');
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [razorpayTestMode, setRazorpayTestMode] = useState(false);
  const [shopPaymentEnabled, setShopPaymentEnabled] = useState(null);
  const [submittingInquiry, setSubmittingInquiry] = useState(false);
  const [simulatingPayment, setSimulatingPayment] = useState(false);
  const [orderFlowPhase, setOrderFlowPhase] = useState(null);
  const [completedOrderResult, setCompletedOrderResult] = useState(null);
  const [completedInquiryResult, setCompletedInquiryResult] = useState(null);
  const [cart, setCart] = useState([]);
  const [buyNowLineItems, setBuyNowLineItems] = useState(null);
  const [itemForm, setItemForm] = useState({ quantity: 1, customRequest: '' });
  const [checkoutForm, setCheckoutForm] = useState({
    customerName: '',
    phone: '',
    deliveryDate: '',
    advanceAmount: 0,
    customRequest: ''
  });
  const [imageViewer, setImageViewer] = useState({ isOpen: false, images: [], currentIndex: 0 });
  const productHeroRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [id]);

  useLockBodyScroll(showModal || showCheckoutModal || imageViewer.isOpen);

  useEffect(() => {
    const parsedCart = readShopCart();
    if (parsedCart.length > 0) {
      setCart(parsedCart);
    }
  }, []);

  useEffect(() => {
    writeShopCart(cart);
  }, [cart]);

  useEffect(() => {
    fetch('/api/payments/test')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.testMode) setRazorpayTestMode(true);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    getPublicShopSettings()
      .then((data) => setShopPaymentEnabled(Boolean(data.shopPaymentEnabled)))
      .catch(() => setShopPaymentEnabled(false));
  }, []);

  useEffect(() => {
    const loadItem = async () => {
      try {
        setLoading(true);
        const items = await getPublicInventory();
        setInventory(items);
        const found = items.find((inv) => String(inv.id) === String(id));
        if (!found) {
          toast.error(t('productDetail.modelNotFound'));
          navigate('/');
          return;
        }
        setItem(found);
      } catch (error) {
        toast.error(t('productDetail.loadDetailFailed'));
      } finally {
        setLoading(false);
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      }
    };
    loadItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, navigate]);

  const relatedCategory = useMemo(() => {
    if (location.state?.relatedCategory) return location.state.relatedCategory;
    const stored = sessionStorage.getItem('shopRelatedCategory');
    if (stored) return stored;
    if (!item) return null;
    return itemSize(item);
  }, [location.state, item]);

  const relatedItems = useMemo(() => {
    if (!item || !relatedCategory) return [];
    return inventory
      .filter((inv) => inv.id !== item.id && itemSize(inv) === relatedCategory)
      .sort((a, b) => a.modelNumber - b.modelNumber)
      .slice(0, 4);
  }, [inventory, item, relatedCategory]);

  const shopOrderType = 'retail';
  const itemOutOfStock = item ? isShopItemOutOfStock(item) : false;
  const cartLineUnitPrice = (cartItem) => getShopLineUnitPrice(cartItem, shopOrderType);
  const checkoutCart = buyNowLineItems ?? cart;
  const isBuyNowCheckout = buyNowLineItems != null;

  const calculateGrandTotal = () => calculateShopCartTotal(cart, shopOrderType);
  const calculateCheckoutGrandTotal = () => calculateShopCartTotal(checkoutCart, shopOrderType);
  const calculateTotalAdvance = () =>
    roundRupees(cart.reduce((sum, cartItem) => sum + cartItem.advanceAmount, 0));

  const runOrderModal = (mode) => {
    setItemForm({ quantity: 1, customRequest: '' });
    setOrderModalMode(mode);
    setShowModal(true);
  };

  const openOrderModal = (mode) => {
    if (item && isShopItemOutOfStock(item)) {
      toast.error(t('gallery.stockOutOfStock'));
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    runOrderModal(mode);
  };

  const handleOrderModalClick = (mode, e) => {
    e?.preventDefault();
    if (mode === 'buy') {
      if (item && isShopItemOutOfStock(item)) {
        toast.error(t('gallery.stockOutOfStock'));
        return;
      }
      setItemForm({ quantity: 1, customRequest: '' });
      handleBuyNow();
      return;
    }
    openOrderModal('add');
  };

  const handleAddToCart = () => {
    if (!item) return;
    const quantity = 1;

    if (isShopItemOutOfStock(item)) {
      toast.error(t('gallery.stockOutOfStock'));
      return;
    }

    if (cart.some((cartItem) => cartItem.id === item.id)) {
      toast.error(t('toast.itemAlreadyInCart'));
      return;
    }

    if (quantity > item.currentStock) {
      toast.error(t('toast.notEnoughStock'));
      return;
    }
    const cartItem = {
      ...item,
      quantity,
      customRequest: itemForm.customRequest,
      advanceAmount: 0,
      customerName: '',
      phone: '',
      deliveryDate: ''
    };
    setCart((prev) => [...prev, cartItem]);
    setShowModal(false);
    toast.success(t('productDetail.itemAdded'));
  };

  const handleBuyNow = () => {
    if (!item) return;
    const quantity = 1;

    if (isShopItemOutOfStock(item)) {
      toast.error(t('gallery.stockOutOfStock'));
      return;
    }

    if (quantity > item.currentStock) {
      toast.error(t('toast.notEnoughStock'));
      return;
    }
    const cartItem = {
      ...item,
      quantity,
      customRequest: itemForm.customRequest,
      advanceAmount: 0,
      customerName: '',
      phone: '',
      deliveryDate: '',
    };
    setBuyNowLineItems([cartItem]);
    const totalAmount = calculateShopCartTotal([cartItem], shopOrderType);
    const minimumAdvance = getShopMinimumAdvance(totalAmount);
    setCheckoutForm({
      customerName: '',
      phone: '',
      deliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      advanceAmount: minimumAdvance,
      customRequest: itemForm.customRequest || '',
    });
    setShowModal(false);
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    setShowCheckoutModal(true);
  };

  const handleCheckout = () => {
    setBuyNowLineItems(null);
    const totalAmount = calculateGrandTotal();
    const minimumAdvance = getShopMinimumAdvance(totalAmount);
    setCheckoutForm({
      customerName: '',
      phone: '',
      deliveryDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      advanceAmount: minimumAdvance
    });
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    setShowCheckoutModal(true);
  };

  const closeCheckoutModal = ({ preserveBuyNow = false } = {}) => {
    setShowCheckoutModal(false);
    if (!preserveBuyNow) {
      setBuyNowLineItems(null);
    }
  };

  const resetOrderFlow = () => {
    setOrderFlowPhase(null);
    setCompletedOrderResult(null);
    setCompletedInquiryResult(null);
    setBuyNowLineItems(null);
    setShowCheckoutModal(false);
  };

  const handleOpenInquiryWhatsApp = () => {
    if (!completedInquiryResult) return;
    openShopInquiryWhatsAppFromCart({
      inquiry: completedInquiryResult.inquiry,
      cart: completedInquiryResult.cart,
      customerName: completedInquiryResult.customerName,
      phone: completedInquiryResult.phone,
      deliveryDate: completedInquiryResult.deliveryDate,
      itemName,
      t,
    });
  };

  const runOrderCompletion = async (response, activeCart, { buyNow = false } = {}) => {
    try {
      const result = await completePublicGalleryOrderAfterPayment({
        response,
        activeCart,
        locale,
        orderType: shopOrderType,
        verifyPayment,
        createPublicOrder,
        toast,
        t,
        onPhaseChange: setOrderFlowPhase,
      });

      if (result) {
        setCompletedOrderResult(result);
        setBuyNowLineItems(null);
        if (!buyNow) {
          setCart([]);
          clearShopCart();
        }
        setShowCheckoutModal(false);
      }
    } catch (error) {
      setOrderFlowPhase(null);
      setCompletedOrderResult(null);
      toast.error(error.message || t('toast.orderAfterPaymentFail'));
      throw error;
    }
  };

  const handlePayment = async (advanceAmountOverride = null, cartForCheckout = null, { buyNow = false } = {}) => {
    const activeCart = cartForCheckout ?? cart;

    if (activeCart.length === 0) {
      toast.error(t('toast.cartEmpty'));
      return;
    }
    try {
      const advanceAmount = roundRupees(
        advanceAmountOverride !== null ? advanceAmountOverride : calculateTotalAdvance()
      );
      if (advanceAmount <= 0) {
        toast.error(t('toast.advancePositive'));
        return;
      }
      const paymentOrder = await createPaymentOrder({
        amount: advanceAmount,
        currency: 'INR',
        receipt: `receipt_${Date.now()}`,
        notes: { customerName: activeCart[0].customerName, phone: activeCart[0].phone, itemCount: activeCart.length, paymentType: 'advance' }
      });
      const options = buildRazorpayCheckoutOptions({
        key: paymentOrder.key,
        amount: paymentOrder.amount,
        currency: paymentOrder.currency,
        order_id: paymentOrder.orderId,
        name: t('razorpay.orgName'),
        description:
          activeCart.length === 1
            ? t('razorpay.orderDescription', { count: activeCart.length })
            : t('razorpay.orderDescriptionPlural', { count: activeCart.length }),
        image: '/logo.png',
        prefill: { name: activeCart[0].customerName, contact: activeCart[0].phone, email: '' },
        theme: { color: '#ea580c' },
        handler: async function(response) {
          try {
            await runOrderCompletion(response, activeCart, { buyNow });
          } catch (error) {
            console.error('Order completion error:', error);
          }
        },
        modal: {
          ondismiss: function() {
            toast.error(t('productDetail.paymentCancelled'));
          }
        }
      }, { testMode: paymentOrder.testMode });
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function() {
        toast.error(t('toast.paymentFailed'));
      });
      rzp.open();
    } catch (error) {
      toast.error(error.message || t('toast.paymentInitFailed'));
    }
  };

  const prepareCheckoutCart = () => {
    if (!checkoutForm.customerName.trim()) {
      toast.error(t('toast.nameRequired'));
      return null;
    }
    if (!checkoutForm.phone.trim() || !/^[6-9]\d{9}$/.test(checkoutForm.phone.trim())) {
      toast.error(t('toast.phoneRequired'));
      return null;
    }
    const totalAmount = calculateCheckoutGrandTotal();
    const minimumAdvance = getShopMinimumAdvance(totalAmount);
    const totalAdvance = roundRupees(checkoutForm.advanceAmount);
    if (totalAdvance < minimumAdvance) {
      toast.error(t('productDetail.advanceAtLeast', { amount: minimumAdvance.toLocaleString('en-IN') }));
      return null;
    }
    if (totalAdvance > totalAmount) {
      toast.error(t('productDetail.advanceExceedsTotal'));
      return null;
    }
    const updatedCart = checkoutCart.map((cartItem) => ({
      ...cartItem,
      customerName: checkoutForm.customerName,
      phone: checkoutForm.phone,
      deliveryDate: checkoutForm.deliveryDate,
      advanceAmount: Math.floor(totalAdvance / checkoutCart.length),
      customRequest: checkoutForm.customRequest || cartItem.customRequest || '',
    }));
    const totalDistributed = updatedCart.slice(0, -1).reduce((sum, cartItem) => sum + cartItem.advanceAmount, 0);
    updatedCart[updatedCart.length - 1].advanceAmount = roundRupees(totalAdvance - totalDistributed);
    return { updatedCart, totalAdvance };
  };

  const prepareInquiryCart = () => {
    if (!checkoutForm.customerName.trim()) {
      toast.error(t('toast.nameRequired'));
      return null;
    }
    if (!checkoutForm.phone.trim() || !/^[6-9]\d{9}$/.test(checkoutForm.phone.trim())) {
      toast.error(t('toast.phoneRequired'));
      return null;
    }

    const updatedCart = checkoutCart.map((cartItem) => ({
      ...cartItem,
      customerName: checkoutForm.customerName,
      phone: checkoutForm.phone,
      deliveryDate: checkoutForm.deliveryDate,
      advanceAmount: 0,
      customRequest: checkoutForm.customRequest || cartItem.customRequest || '',
    }));

    return { updatedCart };
  };

  const handleInquirySubmit = async () => {
    const buyNow = isBuyNowCheckout;
    const prepared = prepareInquiryCart();
    if (!prepared) return;

    const { updatedCart } = prepared;

    try {
      setSubmittingInquiry(true);
      setOrderFlowPhase('inquirySubmitting');
      const inquiry = await submitShopInquiryFromCart({
        cart: updatedCart,
        customerName: checkoutForm.customerName,
        phone: checkoutForm.phone,
        locale,
        orderType: shopOrderType,
      });
      setCompletedInquiryResult({
        inquiry,
        cart: updatedCart,
        customerName: checkoutForm.customerName,
        phone: checkoutForm.phone,
        deliveryDate: checkoutForm.deliveryDate,
      });
      setBuyNowLineItems(null);
      if (!buyNow) {
        setCart([]);
        clearShopCart();
      }
      setShowCheckoutModal(false);
      setOrderFlowPhase('inquirySuccess');
    } catch (error) {
      setOrderFlowPhase(null);
      setCompletedInquiryResult(null);
      toast.error(error.message || t('toast.inquiryFailed'));
    } finally {
      setSubmittingInquiry(false);
    }
  };

  const handleCheckoutSubmit = () => {
    if (!shopPaymentEnabled) {
      handleInquirySubmit();
      return;
    }

    const buyNow = isBuyNowCheckout;
    const prepared = prepareCheckoutCart();
    if (!prepared) return;
    const { updatedCart, totalAdvance } = prepared;
    if (!buyNow) {
      setCart(updatedCart);
    }
    closeCheckoutModal({ preserveBuyNow: buyNow });
    handlePayment(totalAdvance, updatedCart, { buyNow });
  };

  const handleSimulateTestPayment = async () => {
    const buyNow = isBuyNowCheckout;
    const prepared = prepareCheckoutCart();
    if (!prepared) return;

    const { updatedCart, totalAdvance } = prepared;

    try {
      setSimulatingPayment(true);
      if (!buyNow) {
        setCart(updatedCart);
      }

      const paymentOrder = await createPaymentOrder({
        amount: totalAdvance,
        currency: 'INR',
        receipt: `receipt_${Date.now()}`,
        notes: {
          customerName: updatedCart[0].customerName,
          phone: updatedCart[0].phone,
          itemCount: updatedCart.length,
          paymentType: 'advance',
        },
      });

      const simulated = await simulateTestPayment({ razorpay_order_id: paymentOrder.orderId });

      await runOrderCompletion(simulated, updatedCart, { buyNow });
    } catch (error) {
      toast.error(error.message || t('toast.orderAfterPaymentFail'));
    } finally {
      setSimulatingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="text-muted-foreground">{t('productDetail.loading')}</p>
        </div>
      </div>
    );
  }

  if (!item) return null;

  return (
    <>
      <ShopToaster />
      <ShopOrderFlowOverlay
        phase={orderFlowPhase}
        orderResult={completedOrderResult}
        inquiryResult={completedInquiryResult}
        onContinue={resetOrderFlow}
        onOpenWhatsApp={
          completedInquiryResult ? handleOpenInquiryWhatsApp : undefined
        }
        onDownloadPdf={
          completedOrderResult?.order?._id
            ? () => downloadShopOrderReceipt(completedOrderResult, 'pdf')
            : undefined
        }
        t={t}
      />
      <div className="min-h-screen bg-background">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-10">
          <motion.div
            className="mb-6 flex flex-wrap items-center justify-between gap-3"
            variants={sectionMotion}
            initial="hidden"
            animate="visible"
          >
            <Link to="/" className="font-medium text-primary hover:text-primary/90">
              {t('productDetail.backToShop')}
            </Link>
            <div className="flex items-center gap-2">
              <div
                role="group"
                aria-label={t('lang.toggleAria')}
                className="inline-flex rounded-xl border border-border/80 bg-muted/60 p-0.5 shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => setLocale('en')}
                  aria-pressed={locale === 'en'}
                  className={cn(
                    'rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors',
                    locale === 'en'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  )}
                >
                  {t('lang.enShort')}
                </button>
                <button
                  type="button"
                  onClick={() => setLocale('mr')}
                  aria-pressed={locale === 'mr'}
                  className={cn(
                    'rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors',
                    locale === 'mr'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  )}
                >
                  {t('lang.mrShort')}
                </button>
                <button
                  type="button"
                  onClick={() => setLocale('kn')}
                  aria-pressed={locale === 'kn'}
                  className={cn(
                    'rounded-lg px-2.5 py-1 text-xs font-semibold transition-colors',
                    locale === 'kn'
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted'
                  )}
                >
                  {t('lang.knShort')}
                </button>
              </div>
              {cart.length > 0 && (
                <button
                  type="button"
                  onClick={handleCheckout}
                  className="rounded-full bg-accent px-5 py-2.5 font-semibold text-accent-foreground shadow-sm transition-colors hover:bg-accent/90"
                >
                  {t('productDetail.checkout', { count: cart.length })}
                </button>
              )}
            </div>
          </motion.div>

          <motion.div
            ref={productHeroRef}
            className="scroll-mt-4 rounded-3xl border border-primary/15 bg-card p-4 shadow-[0_20px_60px_rgba(159,18,57,0.06)] sm:scroll-mt-6 sm:p-8"
            variants={sectionMotion}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-10">
              <div className="self-start lg:sticky lg:top-28">
                <button
                  onClick={() => setImageViewer({ isOpen: true, images: getItemMediaSlides(item), currentIndex: 0 })}
                  className="group relative overflow-hidden rounded-2xl border border-border"
                >
                  <img
                    src={item.photo}
                    alt={itemName(item)}
                    className="aspect-square h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute bottom-3 right-3 rounded-full bg-foreground/70 px-2.5 py-1 text-xs text-background">
                    {t('productDetail.viewGallery')}
                  </span>
                </button>
                <div className="mt-4 grid grid-cols-3 gap-3">
                  <div className="rounded-xl border border-border bg-muted p-3 text-center">
                    <p className="text-xs text-muted-foreground">{t('productDetail.craft')}</p>
                    <p className="text-sm font-medium text-foreground">{t('productDetail.handmade')}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-muted p-3 text-center">
                    <p className="text-xs text-muted-foreground">{t('productDetail.delivery')}</p>
                    <p className="text-sm font-medium text-foreground">{t('productDetail.panIndia')}</p>
                  </div>
                  <div className="rounded-xl border border-border bg-muted p-3 text-center">
                    <p className="text-xs text-muted-foreground">{t('productDetail.payment')}</p>
                    <p className="text-sm font-medium text-foreground">
                      {shopPaymentEnabled === true
                        ? t('productDetail.secure')
                        : t('productDetail.inquiry')}
                    </p>
                  </div>
                </div>

                {/* Mobile only — buttons directly below image */}
                <div className="mt-4 space-y-3 lg:hidden">
                  <button
                    type="button"
                    onClick={(e) => handleOrderModalClick('add', e)}
                    disabled={itemOutOfStock}
                    className={cn(
                      'w-full rounded-full bg-accent px-6 py-3 font-semibold text-accent-foreground shadow-sm transition-colors hover:bg-accent/90',
                      itemOutOfStock && 'cursor-not-allowed opacity-50 hover:bg-accent'
                    )}
                  >
                    {itemOutOfStock ? t('gallery.outOfStockLocked') : t('productDetail.addToCart')}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => handleOrderModalClick('buy', e)}
                    disabled={itemOutOfStock}
                    className={cn(
                      'w-full rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90',
                      itemOutOfStock && 'cursor-not-allowed opacity-50 hover:bg-primary'
                    )}
                  >
                    {itemOutOfStock ? t('gallery.outOfStockLocked') : t('productDetail.buyNow')}
                  </button>
                  {!item.retailOnly && (
                    <div className="space-y-2">
                      <ShopWholesaleMinQtyCallout t={t} />
                      <Link
                        to="/shop/wholesale"
                        className="block w-full rounded-full bg-violet-700 px-6 py-3 text-center font-semibold text-white shadow-sm transition-colors hover:bg-violet-800"
                      >
                        {t('productDetail.wholesaleBasketLink')}
                      </Link>
                    </div>
                  )}
                  <button
                    onClick={() => navigate('/')}
                    className="w-full rounded-xl border border-border px-6 py-3 font-semibold text-foreground transition-colors hover:bg-muted"
                  >
                    {t('productDetail.continueShopping')}
                  </button>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <p className="mb-2 text-sm text-muted-foreground">
                    {t('gallery.modelPrefix')} {formatModelCode(itemSize(item), item.modelNumber)}
                  </p>
                  <h1 className="font-heading mb-3 text-3xl font-semibold tracking-tight text-primary sm:text-4xl">
                    {itemName(item)}
                  </h1>
                  {item.retailOnly && (
                    <span className="mb-3 inline-flex px-2.5 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200">
                      {t('productDetail.retailOnlyBadge')}
                    </span>
                  )}
                  <p className="mb-4 text-3xl font-semibold text-primary sm:text-4xl">
                    ₹{roundRupees(getShopDisplayUnitPrice(item, 'retail')).toLocaleString('en-IN')}
                  </p>
                  {itemOutOfStock ? (
                    <>
                      <p className="mb-1 font-medium text-rose-600">{t('productDetail.outOfStock')}</p>
                      <p className="mb-2 text-sm text-muted-foreground">{t('productDetail.outOfStockHint')}</p>
                    </>
                  ) : (
                    <p className="mb-2 text-muted-foreground">
                      {t('productDetail.stock')} {item.currentStock} {t('productDetail.available')}
                    </p>
                  )}
                  <p className="text-muted-foreground">
                    {t('productDetail.category')} {item.categoryName || t('productDetail.defaultCategory')}
                  </p>
                </div>

                <div className="rounded-2xl border border-border bg-muted/50 p-4">
                  <h3 className="mb-3 font-semibold text-foreground">{t('productDetail.whyTitle')}</h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li>{t('productDetail.why1')}</li>
                    <li>
                      {shopPaymentEnabled === true
                        ? t('productDetail.why2')
                        : t('productDetail.why2Inquiry')}
                    </li>
                    <li>{t('productDetail.why3')}</li>
                  </ul>
                </div>

                {/* Desktop only — buttons shown below image on mobile instead */}
                <div className="hidden lg:block">
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={(e) => handleOrderModalClick('add', e)}
                      disabled={itemOutOfStock}
                      className={cn(
                        'w-full rounded-full bg-accent px-6 py-3 font-semibold text-accent-foreground shadow-sm transition-colors hover:bg-accent/90',
                        itemOutOfStock && 'cursor-not-allowed opacity-50 hover:bg-accent'
                      )}
                    >
                      {itemOutOfStock ? t('gallery.outOfStockLocked') : t('productDetail.addToCart')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleOrderModalClick('buy', e)}
                      disabled={itemOutOfStock}
                      className={cn(
                        'w-full rounded-full bg-primary px-6 py-3 font-semibold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90',
                        itemOutOfStock && 'cursor-not-allowed opacity-50 hover:bg-primary'
                      )}
                    >
                      {itemOutOfStock ? t('gallery.outOfStockLocked') : t('productDetail.buyNow')}
                    </button>
                    {!item.retailOnly && (
                      <div className="space-y-2">
                        <ShopWholesaleMinQtyCallout t={t} />
                        <Link
                          to="/shop/wholesale"
                          className="block w-full rounded-full bg-violet-700 px-6 py-3 text-center font-semibold text-white shadow-sm transition-colors hover:bg-violet-800"
                        >
                          {t('productDetail.wholesaleBasketLink')}
                        </Link>
                      </div>
                    )}
                    <button
                      onClick={() => navigate('/')}
                      className="w-full rounded-xl border border-border py-3 px-6 font-semibold text-foreground transition-colors hover:bg-muted"
                    >
                      {t('productDetail.continueShopping')}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-primary/20 bg-secondary/60 p-3">
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-primary">
                      {t('productDetail.paymentCardTitle')}
                    </p>
                    <p className="text-sm text-secondary-foreground">
                      {shopPaymentEnabled === true
                        ? t('productDetail.paymentCard')
                        : t('productDetail.inquiryCard')}
                    </p>
                  </div>
                  <div className="rounded-xl border border-accent/25 bg-muted p-3">
                    <p className="mb-1 text-xs font-medium uppercase tracking-wide text-accent">
                      {t('productDetail.supportCardTitle')}
                    </p>
                    <p className="text-sm text-foreground">{t('productDetail.supportCard')}</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {relatedItems.length > 0 && (
            <motion.div
              className="mt-12 scroll-mt-24"
              variants={sectionMotion}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: '-80px' }}
            >
              <h2 className="font-heading mb-4 text-2xl font-semibold text-primary">{t('productDetail.relatedModels')}</h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {relatedItems.map((related) => (
                  <button
                    key={related.id}
                    onClick={() => navigate(`/shop/${related.id}`, { state: { relatedCategory } })}
                    className="overflow-hidden rounded-2xl border border-border bg-card text-left shadow-sm transition-all duration-300 hover:shadow-lg"
                  >
                    <div className="relative">
                      <img src={related.photo} alt={itemName(related)} className="h-44 w-full object-cover" />
                      {related.retailOnly && (
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-100 text-amber-800 border border-amber-200">
                          {t('productDetail.retailOnlyBadge')}
                        </div>
                      )}
                    </div>
                    <div className="p-3">
                      <p className="text-xs text-muted-foreground">
                        {t('gallery.modelPrefix')} {formatModelCode(itemSize(related), related.modelNumber)}
                      </p>
                      <p className="line-clamp-1 font-semibold text-foreground">{itemName(related)}</p>
                      <p className="font-semibold text-primary">₹{roundRupees(related.price).toLocaleString('en-IN')}</p>
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          <motion.div
            className="mt-12 rounded-2xl border border-border bg-card p-6"
            variants={sectionMotion}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
          >
            <h3 className="font-heading mb-4 text-xl font-semibold text-primary">{t('productDetail.testimonialsTitle')}</h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-sm text-muted-foreground">{t('productDetail.testimonial1')}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-sm text-muted-foreground">{t('productDetail.testimonial2')}</p>
              </div>
              <div className="rounded-xl border border-border bg-muted/50 p-4">
                <p className="text-sm text-muted-foreground">{t('productDetail.testimonial3')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {showModal && item && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
            aria-hidden="true"
          />

          <div
            className={cn(
              'fixed z-50 overflow-y-auto bg-card text-card-foreground shadow-2xl',
              'bottom-0 left-0 right-0 max-h-[95dvh] rounded-t-2xl',
              'sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
              'sm:w-full sm:max-w-lg sm:max-h-[95vh] sm:rounded-2xl sm:border sm:border-border'
            )}
            role="dialog"
            aria-modal="true"
          >
            <div className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full bg-muted-foreground/30 sm:hidden" />

            <div className="p-3 sm:p-4">
              <div className="mb-3 flex items-start justify-between">
                <h2 className="pr-2 text-base font-bold leading-tight text-foreground sm:text-xl">
                  {orderModalMode === 'buy'
                    ? t('productDetail.buyNow')
                    : t('productDetail.addToCart')}
                </h2>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-shrink-0 rounded p-1 text-xl text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  ✕
                </button>
              </div>

              <div className="mb-3 flex items-start gap-2">
                <div className="w-[88px] shrink-0 sm:w-[100px]">
                  <div className="aspect-square overflow-hidden rounded-lg shadow-md">
                    <img
                      src={item.photo}
                      alt={itemName(item)}
                      className="h-full w-full object-cover"
                    />
                  </div>
                </div>
                <div className="min-w-0 flex flex-1 flex-col justify-center gap-1">
                  <p className="text-xs text-muted-foreground">
                    {t('gallery.modelPrefix')} {formatModelCode(itemSize(item), item.modelNumber)}
                  </p>
                  <h3 className="text-sm font-bold leading-tight text-foreground">
                    {itemName(item)}
                  </h3>
                  {item.retailOnly && (
                    <span className="inline-flex w-fit rounded-full border border-amber-200 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
                      {t('productDetail.retailOnlyBadge')}
                    </span>
                  )}
                  <p className="text-lg font-bold text-primary sm:text-xl">
                    ₹{roundRupees(getShopDisplayUnitPrice(item, 'retail')).toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {itemOutOfStock
                      ? t('productDetail.outOfStock')
                      : `${t('orderModal.stockLabel')} ${item.currentStock} ${t('orderModal.available')}`}
                  </p>
                </div>
              </div>

              <div className="mb-3">
                <label className="mb-1 block text-sm font-medium text-foreground">
                  {t('orderModal.customRequest')}
                </label>
                <textarea
                  value={itemForm.customRequest}
                  onChange={(e) => setItemForm((prev) => ({ ...prev, customRequest: e.target.value }))}
                  placeholder={t('orderModal.customPlaceholder')}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  rows="1"
                />
              </div>

              <div className="sticky bottom-0 bg-card pb-[max(1rem,env(safe-area-inset-bottom))] pt-2 sm:static sm:pb-0">
                <button
                  type="button"
                  onClick={orderModalMode === 'buy' ? handleBuyNow : handleAddToCart}
                  className="w-full rounded-full bg-accent px-6 py-3 font-bold text-accent-foreground shadow-sm hover:bg-accent/90"
                >
                  {orderModalMode === 'buy'
                    ? shopPaymentEnabled === true
                      ? t('productDetail.continueToCheckout')
                      : t('productDetail.continueToInquiry')
                    : t('productDetail.addToCart')}
                </button>
              </div>
            </div>
          </div>
        </>
      )}

      {imageViewer.isOpen && (
        <ProductMediaViewer
          open={imageViewer.isOpen}
          onClose={() => setImageViewer({ isOpen: false, images: [], currentIndex: 0 })}
          item={item}
          images={imageViewer.images}
          initialIndex={imageViewer.currentIndex}
          onIndexChange={(index) => setImageViewer((prev) => ({ ...prev, currentIndex: index }))}
          addToCartLabel={t('productDetail.addToCart')}
          displayName={item ? itemName(item) : undefined}
          onAddToCart={(e) => {
            e?.preventDefault?.();
            setImageViewer({ isOpen: false, images: [], currentIndex: 0 });
            openOrderModal('add');
          }}
        />
      )}

      {showCheckoutModal && (
        <>
          <div
            className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
            onClick={() => closeCheckoutModal()}
            aria-hidden="true"
          />

          <div
            className={cn(
              'fixed z-50 overflow-y-auto bg-card text-card-foreground shadow-2xl',
              'bottom-0 left-0 right-0 max-h-[95dvh] rounded-t-2xl',
              'sm:bottom-auto sm:left-1/2 sm:top-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2',
              'sm:w-full sm:max-w-md sm:max-h-[95vh] sm:rounded-2xl sm:border sm:border-border'
            )}
            role="dialog"
            aria-modal="true"
          >
            <div className="mx-auto mt-3 mb-1 h-1 w-10 rounded-full bg-muted-foreground/30 sm:hidden" />

            <div className="p-3 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-4 sm:pb-4">
              <div className="mb-3 flex items-start justify-between">
                <h2 className="pr-2 text-lg font-bold text-foreground">{t('checkoutModal.title')}</h2>
                <button
                  type="button"
                  onClick={() => closeCheckoutModal()}
                  className="flex-shrink-0 rounded p-1 text-xl text-muted-foreground hover:text-foreground"
                >
                  ✕
                </button>
              </div>
              <div className="mb-3 rounded-lg bg-muted p-3">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold">{t('checkoutModal.summaryTitle')}</h3>
                  <span className="inline-flex rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold capitalize text-blue-800">
                    {t('checkoutModal.orderTypeRetail')}
                  </span>
                </div>
                <div className="space-y-1">
                  {checkoutCart.map((cartItem, index) => (
                    <div key={index} className="flex justify-between text-sm">
                      <span>
                        {itemName(cartItem)} × {cartItem.quantity}
                      </span>
                      <span>₹{roundRupees(cartLineUnitPrice(cartItem) * cartItem.quantity).toLocaleString('en-IN')}</span>
                    </div>
                  ))}
                  <div className="mt-1 border-t border-border pt-1">
                    <div className="flex justify-between text-sm font-semibold">
                      <span>{t('checkoutModal.totalAmount')}</span>
                      <span>₹{calculateCheckoutGrandTotal().toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('checkoutModal.customRequest')}</label>
                  <textarea
                    value={checkoutForm.customRequest}
                    onChange={(e) => setCheckoutForm((prev) => ({ ...prev, customRequest: e.target.value }))}
                    placeholder={t('orderModal.customPlaceholder')}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    rows={1}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('checkoutModal.fullName')}</label>
                  <input
                    type="text"
                    value={checkoutForm.customerName}
                    onChange={(e) => setCheckoutForm((prev) => ({ ...prev, customerName: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder={t('checkoutModal.namePlaceholder')}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('checkoutModal.phone')}</label>
                  <input
                    type="tel"
                    value={checkoutForm.phone}
                    onChange={(e) => setCheckoutForm((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    placeholder={t('checkoutModal.phonePlaceholder')}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">{t('checkoutModal.deliveryDate')}</label>
                  <input
                    type="date"
                    value={checkoutForm.deliveryDate}
                    onChange={(e) => setCheckoutForm((prev) => ({ ...prev, deliveryDate: e.target.value }))}
                    className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                </div>
                {shopPaymentEnabled ? (
                  <div>
                    <label className="mb-1 block text-sm font-medium text-foreground">{t('checkoutModal.advanceLabel')}</label>
                    <input
                      type="number"
                      min="1"
                      max={calculateCheckoutGrandTotal()}
                      value={checkoutForm.advanceAmount}
                      onChange={(e) => setCheckoutForm((prev) => ({ ...prev, advanceAmount: roundRupees(parseFloat(e.target.value) || 0) }))}
                      className="w-full rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                  </div>
                ) : null}
                <div className="rounded-lg border border-border bg-muted p-2">
                  {shopPaymentEnabled ? (
                    <>
                      <p className="whitespace-pre-line text-xs text-muted-foreground">
                        {t('checkoutModal.paymentBlurb', {
                          advance: checkoutForm.advanceAmount.toLocaleString('en-IN'),
                        })}
                      </p>
                      {razorpayTestMode ? (
                        <p className="mt-1 border-t border-border/60 pt-1 text-xs text-muted-foreground">
                          {t('checkoutModal.testPaymentHint')}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-muted-foreground">{t('checkoutModal.paymentDisabledHint')}</p>
                      <p className="mt-1 whitespace-pre-line text-xs text-muted-foreground">{t('checkoutModal.inquiryBlurb')}</p>
                    </>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleCheckoutSubmit}
                  disabled={simulatingPayment || submittingInquiry || shopPaymentEnabled === null}
                  className="w-full rounded-full bg-accent px-6 py-2.5 font-bold text-accent-foreground shadow-sm hover:bg-accent/90 disabled:opacity-50"
                >
                  {shopPaymentEnabled
                    ? t('productDetail.proceedPayment')
                    : t('checkoutModal.submitInquiry')}
                </button>
                {shopPaymentEnabled && razorpayTestMode ? (
                  <button
                    type="button"
                    onClick={handleSimulateTestPayment}
                    disabled={simulatingPayment || submittingInquiry}
                    className="w-full rounded-full border border-border bg-background px-6 py-2.5 text-sm font-semibold text-foreground hover:bg-muted disabled:opacity-50"
                  >
                    {simulatingPayment ? 'Processing…' : t('checkoutModal.simulateTestPayment')}
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </>
      )}

    </>
  );
};

export default ProductDetailPage;
